import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { useOptionalToast } from '../Toast';
// Loaded on first save: lib/save-layers pulls in the API client and the storage client.
const uploadGeoJsonDataset = (args) => import('../../lib/save-layers').then((m) => m.uploadGeoJsonDataset(args));

/**
 * "Save…" dialog of the Layers panel: the layers in the Viewing (not saved) group, each with a
 * checkbox (all ticked) and an editable name, saved as new datasets of the open project.
 * `layers` are layer-tree entries (`id`, `name`, `geojson`); `upload({ projectId, name, geojson })`
 * resolves to the stored dataset row (default: lib/save-layers). `onSaved([{ layerId, dataset,
 * geojson }])` runs once with the layers that were stored; failures stay listed with their error.
 */
export default function SaveLayersDialog({ open, onClose, layers = [], project, onSaved, upload = uploadGeoJsonDataset }) {
  const { showToast } = useOptionalToast();
  const [rows, setRows] = useState([]); // { id, name, checked, error }
  const [busy, setBusy] = useState(false);

  // Reset only when the dialog opens: edits survive layer-list changes while it is open.
  const layersRef = useRef(layers);
  layersRef.current = layers;
  useEffect(() => {
    if (open) setRows(layersRef.current.map((l) => ({ id: l.id, name: l.name, checked: true, error: null })));
  }, [open]);

  const chosen = rows.filter((r) => r.checked && r.name.trim());
  const patch = (id, p) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...p } : r)));

  const save = async () => {
    if (!project?.id || !chosen.length) return;
    setBusy(true);
    const saved = [];
    for (const r of chosen) {
      const layer = layers.find((l) => l.id === r.id);
      try {
        const dataset = await upload({ projectId: project.id, name: r.name.trim(), geojson: layer.geojson });
        saved.push({ layerId: r.id, dataset, geojson: layer.geojson });
        patch(r.id, { error: null });
      } catch (e) {
        patch(r.id, { error: e?.response?.data?.detail || e?.message || 'Upload failed' });
      }
    }
    setBusy(false);
    if (saved.length) {
      onSaved?.(saved);
      showToast(`Saved ${saved.length} layer${saved.length === 1 ? '' : 's'} to ${project.name || 'the project'}`, 'success');
    }
    if (saved.length === chosen.length) onClose?.();
    else setRows((rs) => rs.filter((r) => !saved.some((s) => s.layerId === r.id)));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose?.(); }}>
      <DialogContent data-testid="save-layers-dialog">
        <DialogHeader>
          <DialogTitle>Save layers to project</DialogTitle>
          <DialogDescription>These layers are only being viewed. Saving stores each one as a project dataset.</DialogDescription>
        </DialogHeader>
        <div style={{ fontSize: 12, margin: '4px 0' }}>
          Project: <strong data-testid="save-layers-project">{project?.name || project?.id || 'none'}</strong>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 280, overflow: 'auto' }}>
          {rows.map((r) => (
            <div key={r.id} data-testid="save-layers-row" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Checkbox checked={r.checked} aria-label={`Save ${r.name}`} disabled={busy} onCheckedChange={(v) => patch(r.id, { checked: !!v })} />
                <input value={r.name} aria-label={`Dataset name for ${r.id}`} disabled={busy} onChange={(e) => patch(r.id, { name: e.target.value })}
                  style={{ flex: 1, height: 28, fontSize: 12, padding: '0 8px', borderRadius: 6, color: 'var(--gs-text-0)', background: 'var(--gs-bg-4)', border: '1px solid var(--gs-border)', outline: 'none' }} />
              </div>
              {r.error && <div role="alert" style={{ fontSize: 11, color: '#f87171', paddingLeft: 24 }}>{r.error}</div>}
            </div>
          ))}
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} disabled={busy} className="px-3 py-1 text-sm border rounded-md">Cancel</button>
          <button type="button" onClick={save} disabled={busy || !chosen.length || !project?.id} data-testid="save-layers-confirm"
            className="px-3 py-1 text-sm rounded-md bg-primary text-primary-foreground disabled:opacity-50">
            {busy ? 'Saving…' : `Save ${chosen.length} layer${chosen.length === 1 ? '' : 's'}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
